<template>
    <div ref="nav-container" class="navigation-tree">
        <!-- breadcrumbs: -->
        <nav class="navigation-tree__breadcrumb-nav">
            <button
                class="navigation-tree__refresh-button"
                type="button"
                title="Refresh Data"
                @click="refreshData"
            >
                <font-awesome-icon :icon="['far', 'sync']" size="xs" />
            </button>

            <button
                class="navigation-tree__back-button"
                type="button"
                title="Back"
                @click="onBreadcrumbBackButtonClick"
            >
                <font-awesome-icon :icon="['far', 'chevron-left']" size="sm" />
            </button>

            <ul class="navigation-tree__breadcrumb-list">
                <li
                    v-for="(item, index) in selectedItems"
                    :key="item.id"
                    class="navigation-tree__breadcrumb-list-item"
                    @click="onBreadcrumbClick(index)"
                >
                    <i class="navigation-tree__breadcrumb-chevron">
                        <font-awesome-icon
                            v-if="index > 0"
                            aria-hidden="true"
                            :icon="['fal', 'chevron-right']"
                        />
                    </i>

                    <span>{{ item.name | ellipses(30) }}</span>
                </li>
            </ul>
        </nav>

        <!-- nav tree (left-side): -->
        <!-- N.b. since we have several different nav-trees in the app, we use the `itemType` as class here so that
            `v-scroll-to` directive can use the proper container element for scrolling: -->
        <nav
            class="navigation-tree__nav-tree"
            :class="itemType"
            tabindex="0"
            @keydown.up.down.left.right="onKeydown"
        >
            <!-- load spinner: -->
            <div v-if="isLoading">
                <loading-component />
            </div>

            <!-- tree column: -->
            <div
                v-for="(column, columnIndex) in navTree"
                :key="`column-${columnIndex}`"
                v-scroll-to="{
                    el: `.tree-column--${columnIndex}`,
                    container: `.${ itemType }.navigation-tree__nav-tree`,
                    duration: 700,
                    offset: -135,
                    force: true,
                    x: true,
                    y: false,
                }"
                class="navigation-tree__tree-column"
                :class="treeColumnCssObject(columnIndex)"
            >
                <!-- tree items (individual items inside columns): -->
                <navigation-tree-item
                    v-for="(item, itemIndex) in column"
                    :key="item.id"
                    :item="item"
                    :item-index="itemIndex"
                    :column-index="columnIndex"
                    :class="navItemCssObject(columnIndex, itemIndex)"
                    @click.native="onItemClick(columnIndex, itemIndex)"
                />

                <!-- last item in column is always 'add new item' but first column should not have: -->
                <div
                    v-if="(itemType === 'content') && !isContentSelect && columnIndex > 0 && columnAddButtonAllowed[columnIndex]"
                    class="navigation-tree__new-item-link"
                >
                    <button type="button" @click="createNewItem(null, columnIndex, itemType)">+ NEW ITEM</button>
                </div>

                <div
                    v-if="(itemType === 'content') && !isContentSelect && columnIndex > 0 && columnAddButtonAllowed[columnIndex]"
                    class="navigation-tree__new-item-link"
                >
                    <button type="button" @click="createNewItem(null, columnIndex, itemType, 'folder')">+ NEW FOLDER</button>
                </div>

                <div
                    v-if="(itemType === 'media') && !isContentSelect && columnIndex > 0 && columnAddButtonAllowed[columnIndex]"
                    class="navigation-tree__new-item-link"
                >
                    <button type="button" @click="createNewItem(null, columnIndex, itemType)">+ NEW FOLDER</button>
                </div>
            </div>
            <!-- /tree column -->
        </nav>

        <!-- meta content (right side): -->
        <aside class="navigation-tree__meta-content">
            <div v-if="isItemSelected">
                <div v-if="isLoadingItemData">
                    <loading-component />
                </div>

                <content-data
                    v-else-if="itemType === 'content' && selectedItemData"
                    :item-type="itemType"
                    :data="selectedItemData"
                    :is-content-select="isContentSelect"
                    @select-content="setSelectedContent"
                    @edit="onItemEditClick"
                    @add="onItemAddClick"
                    @addfolder="onFolderAddClick"
                    @delete="onItemDeleteClick"
                />

                <media-data
                    v-else-if="itemType === 'media'"
                    :data="selectedItemData"
                    @select-media="setMedia"
                    @selectNavItem="selectMediaItem"
                />
            </div>

            <slot v-else name="meta-content" />
        </aside>

        <modal
            v-if="isDeleteOptionVisible"
            title="DELETE"
            header-bg-variant="blue-600"
            header-text-variant="white"
            :hide-header-close="true"
            @cancel="continueDelete(false)"
            @ok="continueDelete(true)"
        >
            <div class="delete-options-body">
                <input
                    id="itemDeleteLeaveLinks"
                    v-model="deleteOptionSelection"
                    type="radio"
                    value="0"
                >

                <label for="itemDeleteLeaveLinks">Leave links</label>
                <br>
                <input
                    id="itemDeleteRemoveLinks"
                    v-model="deleteOptionSelection"
                    type="radio"
                    value="1"
                >

                <label for="itemDeleteRemoveLinks">Remove links</label>
                <br>
                <input
                    id="itemDeleteRelink"
                    v-model="deleteOptionSelection"
                    type="radio"
                    value="2"
                >

                <label for="itemDeleteRelink">Link to another item</label>
                <br>
                <input
                    v-model="deleteOptionRelinkPathOrId"
                    type="text"
                    placeholder="Path or ID"
                    class="delete-options__replace"
                    @focus="deleteOptionSelection = 2"
                >
            </div>
            <template v-slot:modal-footer="slotProps">
                <button class="flex-modal__button flex-modal__button--cancel" @click="slotProps.hide('cancel')">
                    Cancel
                </button>

                <button class="flex-modal__button flex-modal__button--continue" @click="slotProps.hide('ok')">
                    Continue
                </button>
            </template>
        </modal>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import {
    HIDE_DRAWER,
    SET_SELECTED_MEDIA,
    SET_NEW_PAGE_PATH,
    SET_SELECTED_CONTENT,
    SET_PRESELECTED_ITEM_ID,
    SHOW_ALERT,
    SHOW_DRAWER,
    SHOW_PROMPT,
} from '@/store/types/mutation-types';

import { getCacheData, setCacheData, clearCacheData } from '@/utils/session-cache';
import keyCodes from '@/utils/key-codes';
import LoadingComponent from '@/components/shared/loading/loading-component';
import NavigationTreeItem from '@/components/page-editor/navigation-tree/navigation-tree-item';
import ContentData from '@/components/page-editor/navigation-tree/content-data';
import MediaData from '@/components/page-editor/navigation-tree/media-data';
import * as service from '@/sitecore/sitecore-data-service';

let globalFetchAndFlashNonce;

export default {
    name: 'navigation-tree',

    components: {
        ContentData,
        LoadingComponent,
        MediaData,
        NavigationTreeItem,
    },

    props: {
        // Can be content or media
        itemType: {
            type: String,
            default: 'content',
        },

        // need way to differentiate
        // load page and select content
        isContentSelect: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            isLoading: true,
            isLoadingItemData: false,

            itemToEdit: {}, // edit/delete item
            navTree: [], // selected branches (i.e. selected columns and their children/subset)
            selectedItems: [], // individual selected items in nav-tree (e.g. 'Home')
            selectedItemData: {},

            data: {},
            cacheStatus: false,

            deleteOptionSelection: 0,
            deleteOptionRelinkPathOrId: '',
            isDeleteOptionVisible: false,
            columnAddButtonAllowed: [],
        };
    },

    computed: {
        ...mapState({
            changedData: state => state.page.changedData,
            drawer: state => state.page.drawer,
            mediaLibraryType: state => state.page.mediaLibraryType,
            preselectedItemId: state => state.page.preselectedItemId,
            pageCreated: state => state.page.pageCreated,
        }),

        isItemSelected() {
            return (
                this.selectedItems.length
                && (this.selectedItems[this.selectedItems.length - 1] || this.itemType === 'media')
            );
        },

        storageKey() {
            return `flex-${ this.itemType }`;
        },
    },

    watch: {
        mediaLibraryType() {
            if (this.itemType !== 'media') return;

            clearCacheData(this.storageKey);
            this.clearData();
        },

        pageCreated() {
            if (this.itemType === 'content') {
                this.refreshData();
            }
        },
    },

    async created() {
        const { data } = this.drawer;

        if (data && data.id) {
            this.setPreselectedItemId(data.id);
            await this.refreshData();
        } else {
            await this.loadData();
        }
    },

    methods: {
        ...mapMutations({
            hideDrawer: HIDE_DRAWER,
            setNewPagePath: SET_NEW_PAGE_PATH,
            setSelectedMedia: SET_SELECTED_MEDIA,
            setSelectedContentStore: SET_SELECTED_CONTENT,
            setPreselectedItemId: SET_PRESELECTED_ITEM_ID,
            showAlert: SHOW_ALERT,
            showDrawer: SHOW_DRAWER,
            showPrompt: SHOW_PROMPT,
        }),

        async loadData() {
            if (!this.navTree.length) {
                this.isLoading = true;

                switch (this.itemType) {
                    case 'content': {
                        await this.getContentTree();
                        break;
                    }
                    case 'media': {
                        await this.getMediaTree();
                        break;
                    }
                    default: {
                        await this.getContentTree();
                        break;
                    }
                }

                this.navTree.push([Object.assign({}, this.data)]);
            }

            if (this.preselectedItemId) {
                await this.setPreselectedItem();
            }

            this.isLoading = false;
        },

        async setPreselectedItem() {
            const pathStack = [];
            const findItem = function findItem(id, currentItem) {
                if (id === currentItem.id) {
                    return true;
                }
                if (!currentItem.children) return false;

                for (let i = 0; i < currentItem.children.length; i++) {
                    const found = findItem(id, currentItem.children[i]);
                    if (found) {
                        pathStack.push({ index: i });
                        return true;
                    }
                }
            };

            findItem(this.preselectedItemId, this.data);
            this.addToNavTree(0, 0); // open media libary item first

            let colIndex = 0;
            while (pathStack.length) {
                colIndex++; // start at 1
                const item = pathStack.pop();
                this.addToNavTree(colIndex, item.index);
            }

            await this.$nextTick();
            this.getItem(this.preselectedItemId);
            this.setPreselectedItemId(''); // clear preselectedItemId
        },

        async getContentTree() {
            const cachedData = getCacheData(this.storageKey);

            if (cachedData) {
                this.data = cachedData;
            } else {
                const response = await service.getContentTree();

                if (response.status === 'error') {
                    this.showAlert({
                        description: `Error getting content tree: ${ response.error }`,
                    });

                    return;
                }

                this.data = response.data;
                setCacheData(response.data, this.storageKey);
            }
        },

        async getMediaTree() {
            const cachedData = getCacheData(this.storageKey);

            if (cachedData) {
                this.data = cachedData;
            } else {
                const response = await service.getMediaTree(this.mediaLibraryType); // currently only looking for images

                if (response.status === 'error') {
                    this.showAlert({
                        description: `Error getting media tree: ${ response.error }`,
                    });

                    return;
                }

                this.data = response.data;
                setCacheData(response.data, this.storageKey);
            }
        },

        async getItem(id) {
            const localNonce = id;
            globalFetchAndFlashNonce = localNonce;

            this.isLoadingItemData = true;

            let response;

            switch (this.itemType) {
                case 'content': {
                    response = await service.getContentItem(id);
                    break;
                }
                case 'media': {
                    response = await service.getMediaItem(id);
                    break;
                }
                default: {
                    response = await service.getContentItem(id);
                    break;
                }
            }

            if (localNonce !== globalFetchAndFlashNonce) { return; }

            if (response.status === 'error') {
                this.showAlert({
                    description: `Error getting item data: ${ response.error }`,
                });

                return;
            }
            this.selectedItemData = response.data;
            this.isLoadingItemData = false;
        },

        async deletePage(itemToDelete, option, pathOrId) {
            const response = await service.deletePage(
                itemToDelete.id,
                option,
                pathOrId,
            );

            if (response.status === 'error') {
                this.showAlert({
                    description: `Error deleting item: ${ response.error }`,
                });

                this.$root.$emit('flex::delete-content::complete');

                return;
            }

            // delete the item from the model/DOM
            this.navTree[itemToDelete.columnIndex].splice(
                itemToDelete.itemIndex,
                1,
            );

            this.navTree = this.navTree.slice(0, itemToDelete.columnIndex + 1);
            this.selectedItems = this.selectedItems.slice(
                0,
                itemToDelete.columnIndex,
            );

            this.$root.$emit('flex::delete-content::complete');
        },

        async refreshData() {
            clearCacheData(this.storageKey);
            this.clearData();
            this.navTree = [];
            await this.loadData();
        },

        onKeydown(event) {
            // TODO: maybe can refactor/reuse some of the code -- esp. code that adds/selects a new item:
            // select root/home if no items have been selected:
            if (this.selectedItems.length === 0) {
                const rootItem = this.navTree[0][0];

                if (rootItem.children.length) {
                    this.addItemChildrenToTree(rootItem, 0);
                }
                this.appendSelectedItem(rootItem, 0);
                this.getItem(rootItem.id);
            } else {
                const lastColumnIndex = this.selectedItems.length - 1;
                const lastColumn = this.navTree[lastColumnIndex];
                const itemIndex = lastColumn.findIndex(
                    item => item.id === this.selectedItems[lastColumnIndex].id,
                );
                const lastItem = lastColumn[itemIndex];

                switch (event.keyCode) {
                    case keyCodes.DOWN:
                        // if selected item is NOT last item in column:
                        if (itemIndex < lastColumn.length - 1) {
                            this.selectedItems.splice(
                                lastColumnIndex,
                                1,
                                lastColumn[itemIndex + 1],
                            );
                            this.onItemClick(lastColumnIndex, itemIndex + 1);
                        }
                        break;
                    case keyCodes.UP:
                        // if selected item is NOT first item in column:
                        if (itemIndex > 0) {
                            this.selectedItems.splice(
                                lastColumnIndex,
                                1,
                                lastColumn[itemIndex - 1],
                            );
                            this.onItemClick(lastColumnIndex, itemIndex - 1);
                        }
                        break;
                    case keyCodes.RIGHT:
                        if (lastItem.children.length) {
                            const nextItem = lastItem.children[0];

                            if (nextItem.children.length) {
                                this.addItemChildrenToTree(
                                    nextItem,
                                    lastColumnIndex + 1,
                                );
                            }
                            this.appendSelectedItem(
                                nextItem,
                                lastColumnIndex + 1,
                            );
                            this.getItem(nextItem.id);
                        }
                        break;
                    case keyCodes.LEFT:
                        if (lastColumnIndex !== 0) {
                            // we want to go back to previous selected item; but since `selectedItems` doesn't have the
                            // complete object, we use the item's id to query it from `navTree`:
                            const prevItemId = this.selectedItems[
                                lastColumnIndex - 1
                            ].id;
                            const prevItem = this.navTree[
                                lastColumnIndex - 1
                            ].find(item => item.id === prevItemId);

                            if (prevItem.children.length) {
                                this.addItemChildrenToTree(
                                    prevItem,
                                    lastColumnIndex - 1,
                                );
                            }
                            this.appendSelectedItem(
                                prevItem,
                                lastColumnIndex - 1,
                            );
                            this.getItem(prevItem.id);
                        }
                        break;
                    default:
                        break;
                }
            }
        },

        onBreadcrumbClick(index) {
            this.navTree = this.navTree.slice(0, index + 2);
            this.selectedItems = this.selectedItems.slice(0, index + 1);
            this.onItemClick(index, 0);
        },

        onBreadcrumbBackButtonClick() {
            if (this.navTree.length > 1) {
                this.selectedItems = this.selectedItems.slice(0, -1);
                this.navTree = this.navTree.slice(0, -1);
            }
        },

        treeColumnCssObject(columnIndex) {
            const classes = [`tree-column--${ columnIndex }`];

            if (columnIndex < this.itemToEdit.columnIndex) {
                classes.push('tree-column--inactive');
            }

            return classes;
        },

        // n.b. in Vue, we can't create computed properties for iterated items; hence, this is a method:
        navItemCssObject(columnIndex, itemIndex) {
            // return css class if item is selected:
            if (this.selectedItems[columnIndex]
                && (this.selectedItems[columnIndex].name === this.navTree[columnIndex][itemIndex].name)
            ) {
                return columnIndex === this.selectedItems.length - 1
                    ? 'navigation-tree__item--last-selected' : 'navigation-tree__item--selected';
            }
        },

        onItemAddClick() {
            this.createNewItem(this.itemToEdit);
        },

        onFolderAddClick() {
            this.createNewItem(this.itemToEdit, 0, 'page', 'folder');
        },

        onItemDeleteClick() {
            this.showPrompt({
                title: 'Confirm Deletion',
                description: `Are you sure you want to delete ${ this.itemToEdit.name }?`,
                onContinue: this.confirmDelete,
                onCancel: () => this.$root.$emit('flex::delete-content::complete'),
            });
        },

        confirmDelete() {
            if (this.itemToEdit.hasItemReferences) {
                this.isDeleteOptionVisible = true;
            } else {
                this.deletePage(this.itemToEdit, 0, '');
            }
        },

        continueDelete(confirm) {
            this.isDeleteOptionVisible = false;

            if (!confirm) {
                this.resetDeleteOptionModel();
            } else {
                this.deletePage(
                    this.itemToEdit,
                    this.deleteOptionSelection,
                    this.deleteOptionRelinkPathOrId,
                );
                this.resetDeleteOptionModel();
            }
        },

        resetDeleteOptionModel() {
            this.deleteOptionSelection = 0;
            this.deleteOptionRelinkPathOrId = '';
            this.$root.$emit('flex::delete-content::complete');
        },

        // For content, not media
        onItemEditClick() {
            window.location.href = `/editor/${ this.itemToEdit.id }`;
        },

        onItemClick(columnIndex, itemIndex) {
            this.addToNavTree(columnIndex, itemIndex);

            const clickedItem = this.navTree[columnIndex][itemIndex];
            this.getItem(clickedItem.id);

            this.itemToEdit = clickedItem;
            this.itemToEdit.itemIndex = itemIndex;
            this.itemToEdit.columnIndex = columnIndex;
        },

        addToNavTree(columnIndex, itemIndex) {
            const clickedItem = this.navTree[columnIndex][itemIndex];

            if (clickedItem.children.length) {
                this.addItemChildrenToTree(clickedItem, columnIndex);
            } else if (clickedItem.isFolder && this.itemType === 'media') {
                this.addBlankColumn(columnIndex);
            } else {
                this.clearItemChildren(columnIndex);
            }

            this.appendSelectedItem(clickedItem, columnIndex);
        },

        addBlankColumn(columnIndex) {
            const newNavTree = [...this.navTree];

            newNavTree.splice(
                columnIndex + 1, // n.b. the `+1` -- this is b/c we're concerned with the item's children
                newNavTree.length - columnIndex,
                [],
            ); // n.b. this is appending only the shallow copy of the clicked item

            this.navTree = newNavTree;
        },

        addItemChildrenToTree(item, columnIndex) {
            const newNavTree = [...this.navTree];

            newNavTree.splice(
                columnIndex + 1, // n.b. the `+1` -- this is b/c we're concerned with the item's children
                newNavTree.length - columnIndex,
                item.children,
            ); // n.b. this is appending only the shallow copy of the clicked item

            this.navTree = newNavTree;
            this.columnAddButtonAllowed[columnIndex + 1] = item.addChildrenAllowed;
        },

        clearItemChildren(columnIndex) {
            const newNavTree = [...this.navTree];

            this.navTree = newNavTree.slice(0, columnIndex + 1);
        },

        appendSelectedItem(item, columnIndex) {
            const newSelectedItemsList = [...this.selectedItems];

            newSelectedItemsList.splice(
                columnIndex,
                newSelectedItemsList.length - columnIndex,
                // n.b. we don't need the entire object, for the 'selectedItems':
                {
                    id: item.id,
                    name: item.name,
                    path: item.path,
                    isFolder: item.isFolder,
                },
            );

            this.selectedItems = newSelectedItemsList;
        },

        createNewItem(parentItem, index, itemType, typeToCreate = 'page') {
            const callback = () => this.openNewPageDrawer(parentItem, index, itemType, typeToCreate);

            if (!this.changedData) {
                callback();
                return;
            }

            this.showPrompt({
                template: 'unsaved-changes',
                onContinue: callback,
            });
        },

        openNewPageDrawer(parentItem, index, itemType, typeToCreate) {
            let path;

            if (parentItem) {
                path = parentItem.path;
            } else {
                const parentIndex = index - 1;
                path = this.selectedItems[parentIndex].path;
            }

            this.setNewPagePath(path);

            if (itemType === 'media') {
                this.showDrawer('create-media-folder');
            } else if (typeToCreate === 'folder') {
                this.showDrawer('create-folder-drawer');
            } else {
                this.showDrawer('create-page-drawer');
            }
        },

        setMedia(item) {
            this.setSelectedMedia(item);
            this.hideDrawer();
        },

        setSelectedContent(item) {
            this.setSelectedContentStore(item);
            this.hideDrawer();
        },

        selectMediaItem(id) {
            this.refreshData();
            this.setPreselectedItemId(id);
            this.getItem(this.preselectedItemId);
        },

        clearData() {
            this.itemToEdit = {};
            this.navTree = [];
            this.selectedItems = [];
            this.selectedItemData = {};

            this.data = {};
        },
    },
};
</script>

<style lang="scss">
$breadcrumb-height: 32px;

.navigation-tree {
    display: grid;
    width: 100%;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: $breadcrumb-height calc(
            100vh - #{$breadcrumb-height} - #{$drawer-header-height}
        );
    grid-template-areas:
        'breadcrumb breadcrumb'
        'nav-tree meta-content';

    &__breadcrumb-nav {
        font-size: $nav-item-font-size-default;
        grid-area: breadcrumb;
        padding: 4px 10px;
        color: var(--modal-actionMenuColor);
        background-color: var(--modal-actionMenuBackgroundColor);
        border-top: 1px solid var(--modal-actionMenuBorderColor);
        border-bottom: 1px solid var(--modal-actionMenuBorderColor);
        height: $breadcrumb-height;
        display: flex;
        align-items: center;
    }

    &__breadcrumb-list {
        display: flex;
        overflow-x: auto;
        padding: 0;
    }

    &__breadcrumb-list-item {
        flex-shrink: 0;
        list-style: none;
        cursor: pointer;

        & + & {
            margin-left: 20px;
        }

        &:hover {
            text-decoration: underline;
        }
    }

    &__breadcrumb-chevron {
        font-size: 13px;
        margin-right: 20px;
    }

    &__back-button {
        height: $breadcrumb-height - 1px;
        margin-top: 1px;
        border-right: 1px solid var(--modal-actionMenuBorderColor);
        color: currentColor;
        padding-right: 10px;
        font-size: 24px;
        cursor: pointer;
        display: flex;
        align-items: center;
    }

    &__refresh-button {
        height: $breadcrumb-height - 1px;
        margin-top: 1px;
        border-right: 1px solid var(--modal-actionMenuBorderColor);
        color: currentColor;
        padding-right: 10px;
        margin-right: 10px;
        font-size: 24px;
        cursor: pointer;
        display: flex;
        align-items: center;
    }

    &__nav-tree {
        will-change: transform;
        display: flex;
        flex-direction: row;
        grid-area: nav-tree;
        position: relative;
        overflow-x: auto;

        // hide scrollbar:
        scrollbar-width: none; // Firefox
        -ms-overflow-style: none; // IE + Edge
        &::-webkit-scrollbar { // Webkit browsers
            width: 0;
        }
    }

    &__meta-content {
        position: relative;
        grid-area: meta-content;
        border-left: 1px solid var(--modal-dividerColor);
        overflow-y: auto;

        // prevents the client font color from being inherited:
        a {
            color: var(--app-linkColor);

            &:hover {
                color: var(--app-linkHoverColor);
            }
        }
    }

    &__tree-column {
        display: flex;
        flex-direction: column;
        width: $nav-item-width;
        min-width: $nav-item-width;
        will-change: transform;
        overflow-y: auto;

        &.tree-column--inactive {
            width: $nav-item-width / 2;
            min-width: $nav-item-width / 2;
        }
    }

    &__item {
        color: var(--fileExplorer-color);
        background-color: var(--fileExplorer-backgroundColor);

        &--selected {
            color: var(--fileExplorer-hoverColor);
            background-color: var(--fileExplorer-hoverBackgroundColor);
        }

        &--last-selected {
            color: var(--fileExplorer-selectedColor);
            background-color: var(--fileExplorer-selectedBackgroundColor);
        }
    }

    &__chevron-icon {
        color: inherit;
    }

    &__new-item-link {
        padding: 6px 10px 6px 16px;

        button {
            color: var(--fileExplorer-linkColor);
            text-decoration: none;
            font-size: $nav-item-font-size-default;

            &:hover {
                color: var(--fileExplorer-linkHoverColor);
                text-decoration: underline;
            }
        }
    }
}

.delete-options__replace {
    border-width: 1px;
    margin-left: 15px;
    width: 300px;
    margin-top: 5px;
    padding: 2px;
}

.delete-options-body {
    padding: 15px;
}
</style>
